import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent, mergeAttributes } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, List, ListOrdered, Quote, AtSign } from "lucide-react";
import api from "@/lib/api";
import { MentionMenu } from "./MentionMenu";

export const JournalMention = Mention.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      kind: { default: null, parseHTML: (el) => el.getAttribute("data-kind"), renderHTML: (a) => ({ "data-kind": a.kind }) },
      href: { default: null, parseHTML: (el) => el.getAttribute("data-href"), renderHTML: (a) => ({ "data-href": a.href }) },
    };
  },
});

const EMPTY = { scriptures: [], questions: [] };
const norm = (items, prev) => (items && Array.isArray(items.scriptures) ? items : prev || EMPTY);

/* Shared @-mention suggestion config (journal + blog editors). */
export function mentionSuggestion(setMention, menuRef, reqRef) {
  return {
    char: "@",
    allowSpaces: true,
    items: async ({ query }) => {
      const id = ++reqRef.current;
      if (!query.trim()) return EMPTY;
      try {
        const { data } = await api.get("/journal/mention", { params: { q: query } });
        return id === reqRef.current ? data : null;
      } catch { return EMPTY; }
    },
    render: () => ({
      onStart: (props) => setMention({ ...props, items: norm(props.items), loading: false }),
      onUpdate: (props) => setMention((m) => ({ ...m, ...props, items: norm(props.items, m?.items), loading: false })),
      onKeyDown: (props) => {
        if (props.event.key === "Escape") { setMention(null); return true; }
        return menuRef.current?.onKeyDown(props) || false;
      },
      onExit: () => setMention(null),
    }),
  };
}

export const mentionRenderHTML = ({ options, node }) => ["span", mergeAttributes({ class: `journal-mention journal-mention-${node.attrs.kind || "x"}`, "data-type": "mention", "data-id": node.attrs.id }, options.HTMLAttributes), node.attrs.label];

/* Rich journal editor with @-mentions. `value` is a Tiptap JSON doc; onChange receives the new doc. */
export default function JournalEditor({ value, onChange, placeholder = "Write freely. Type @ to link any Bible verse or a question…", autoFocus = false, minHeight = 180, testId = "journal-editor" }) {
  const [mention, setMention] = useState(null);
  const menuRef = useRef(null);
  const reqRef = useRef(0);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, codeBlock: false, code: false, horizontalRule: false, link: false }),
      Placeholder.configure({ placeholder }),
      JournalMention.configure({
        deleteTriggerWithBackspace: true,
        renderText: ({ node }) => node.attrs.label,
        renderHTML: ({ options, node }) => ["span", mergeAttributes({ class: `journal-mention journal-mention-${node.attrs.kind || "x"}`, "data-type": "mention", "data-id": node.attrs.id }, options.HTMLAttributes), node.attrs.label],
        suggestion: {
          char: "@",
          allowSpaces: true,
          items: async ({ query }) => {
            const id = ++reqRef.current;
            if (!query.trim()) return EMPTY;
            try {
              const { data } = await api.get("/journal/mention", { params: { q: query } });
              return id === reqRef.current ? data : null;
            } catch { return EMPTY; }
          },
          render: () => ({
            onStart: (props) => setMention({ ...props, items: norm(props.items), loading: false }),
            onUpdate: (props) => setMention((m) => ({ ...m, ...props, items: norm(props.items, m?.items), loading: false })),
            onKeyDown: (props) => {
              if (props.event.key === "Escape") { setMention(null); return true; }
              return menuRef.current?.onKeyDown(props) || false;
            },
            onExit: () => setMention(null),
          }),
        },
      }),
    ],
    content: value || { type: "doc", content: [{ type: "paragraph" }] },
    autofocus: autoFocus ? "end" : false,
    editorProps: { attributes: { class: "journal-prose focus:outline-none", "data-testid": `${testId}-content` } },
    onUpdate: ({ editor: ed }) => onChange?.(ed.getJSON()),
  });

  useEffect(() => () => editor?.destroy(), [editor]);

  if (!editor) return <div style={{ minHeight }} className="animate-pulse rounded-xl bg-muted" />;

  const Tool = ({ active, onClick, label, icon: Icon }) => (
    <button type="button" onMouseDown={(e) => { e.preventDefault(); onClick(); }} aria-label={label} title={label} data-testid={`${testId}-tool-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`rounded-md p-1.5 transition-colors ${active ? "bg-gold/20 text-gold" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
      <Icon className="h-4 w-4" />
    </button>
  );

  return (
    <div className="rounded-2xl border border-border bg-background" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border px-2 py-1.5">
        <Tool label="Bold" icon={Bold} active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} />
        <Tool label="Italic" icon={Italic} active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} />
        <Tool label="Bullet list" icon={List} active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} />
        <Tool label="Numbered list" icon={ListOrdered} active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
        <Tool label="Quote" icon={Quote} active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} />
        <span className="mx-1 h-4 w-px bg-border" />
        <Tool label="Mention" icon={AtSign} onClick={() => editor.chain().focus().insertContent("@").run()} />
        <span className="ml-auto hidden font-mono text-[10px] uppercase tracking-wider text-muted-foreground sm:block">@ links a verse, question or commentary</span>
      </div>
      <div className="px-4 py-3" style={{ minHeight }} onClick={() => editor.chain().focus().run()}>
        <EditorContent editor={editor} />
      </div>
      {mention && <MentionMenu ref={menuRef} items={mention.items} loading={mention.loading} query={mention.query} clientRect={mention.clientRect} command={mention.command} />}
    </div>
  );
}
